import { useState, useCallback, useEffect, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Edit01Icon } from '@untitledui-icons/react/line'
import { Button as AriaButton } from 'react-aria-components'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/base/buttons'
import { Badge } from '@/components/ui/Badge'
import { Tooltip, TooltipTrigger } from '@/components/ui/Tooltip'
import { useSimRun, useSimRuns, useSimScenarios, useUpdateScenario } from '@/hooks/api/useSimulations'
import { useAssistant } from '@/hooks/api/useAssistants'
import { useAIDeployments } from '@/hooks/api/useAIDeployments'
import { useUsersByIds } from '@/hooks/api/useUsers'
import { StatusBadge, PassFailBadge, ConversationViewer, ScenarioFormDialog } from './components'
import { toast } from '@/lib/toast'
import type { SimScenarioInput } from '@/types/simulation'

/**
 * Run details page - shows detailed information about a simulation run
 */
export function SimRunDetailsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const runId = Number(id)

  // State for scenario edit modal
  const [isScenarioModalOpen, setIsScenarioModalOpen] = useState(false)

  // Fetch run details
  const { data: run, isLoading, error } = useSimRun({ id: runId })
  const { data: scenarios = [] } = useSimScenarios({})
  const { data: assistant } = useAssistant(run?.assistant_id ?? 0, { enabled: !!run?.assistant_id })
  const { data: aiDeployments = [] } = useAIDeployments()

  // Fetch sibling runs for keyboard navigation
  const { data: siblingRuns = [] } = useSimRuns({
    filters: { batch_id: run?.batch_id },
    enabled: !!run?.batch_id,
    pollInterval: false, // No polling needed for navigation
  })

  // Compute previous and next run IDs
  const { prevRunId, nextRunId, currentIndex, totalRuns } = useMemo(() => {
    if (!siblingRuns.length || !run) {
      return { prevRunId: null, nextRunId: null, currentIndex: -1, totalRuns: 0 }
    }
    // Sort runs by ID to ensure consistent ordering
    const sortedRuns = [...siblingRuns].sort((a, b) => a.id - b.id)
    const currentIdx = sortedRuns.findIndex((r) => r.id === run.id)
    return {
      prevRunId: currentIdx > 0 ? sortedRuns[currentIdx - 1].id : null,
      nextRunId: currentIdx < sortedRuns.length - 1 ? sortedRuns[currentIdx + 1].id : null,
      currentIndex: currentIdx,
      totalRuns: sortedRuns.length,
    }
  }, [siblingRuns, run])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't navigate if user is typing in an input or textarea
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      if (e.key === 'ArrowLeft' && prevRunId) {
        navigate(`/simulations/runs/${prevRunId}`)
      } else if (e.key === 'ArrowRight' && nextRunId) {
        navigate(`/simulations/runs/${nextRunId}`)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [prevRunId, nextRunId, navigate])

  // Mutations
  const updateScenario = useUpdateScenario()

  // Fetch user who created this run (if available)
  const createdByIds = run?.created_by_id ? [run.created_by_id] : []
  const { data: createdByUsers = [] } = useUsersByIds(createdByIds, createdByIds.length > 0)
  const createdByUser = createdByUsers[0]

  // Get display names
  const scenario = run ? scenarios.find((s) => s.id === run.scenario_id) : null

  // Get AI deployment names
  const aiDeployment = run?.assistant_ai_deployment_id
    ? aiDeployments.find((d) => d.id === run.assistant_ai_deployment_id)
    : null
  const simulatorDeployment = run?.simulator_ai_deployment_id
    ? aiDeployments.find((d) => d.id === run.simulator_ai_deployment_id)
    : null
  const judgeDeployment = run?.judge_ai_deployment_id
    ? aiDeployments.find((d) => d.id === run.judge_ai_deployment_id)
    : null

  const handleBack = () => {
    if (run?.batch_id) {
      navigate(`/simulations/batches/${run.batch_id}`)
    } else {
      navigate('/simulations/batches')
    }
  }

  // Handler for opening scenario edit modal
  const handleEditScenario = useCallback(() => {
    setIsScenarioModalOpen(true)
  }, [])

  // Handler for scenario form submission
  const handleScenarioSubmit = useCallback(
    async (data: SimScenarioInput) => {
      if (!scenario) return
      try {
        await updateScenario.mutateAsync({ id: scenario.id, data })
        toast.success(t('pages.simulations.scenarios.update_success'))
        setIsScenarioModalOpen(false)
      } catch {
        toast.error(t('pages.simulations.scenarios.update_error'))
      }
    },
    [scenario, updateScenario, t]
  )

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    )
  }

  if (error || !run) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400">
          {t('pages.simulations.run_details.not_found')}
        </p>
        <Button color="secondary" onClick={handleBack} className="mt-4">
          {t('pages.simulations.run_details.back_to_runs')}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('pages.simulations.run_details.title', { id: run.id })}
        subtitle={scenario?.name ?? t('resources.simulations.scenario_fallback', { id: run.scenario_id })}
        backButton={{
          label: t('pages.simulations.run_details.back_to_runs'),
          onClick: handleBack,
        }}
        actions={
          totalRuns > 1 && (
            <TooltipTrigger delay={0}>
              <AriaButton className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700">
                <span>{t('pages.simulations.run_details.run_position', { current: currentIndex + 1, total: totalRuns })}</span>
                <span className="flex gap-1">
                  <kbd className="rounded border border-gray-300 bg-gray-100 px-1.5 py-0.5 text-xs font-medium dark:border-gray-600 dark:bg-gray-700">←</kbd>
                  <kbd className="rounded border border-gray-300 bg-gray-100 px-1.5 py-0.5 text-xs font-medium dark:border-gray-600 dark:bg-gray-700">→</kbd>
                </span>
              </AriaButton>
              <Tooltip>
                {t('pages.simulations.run_details.keyboard_navigation_hint')}
              </Tooltip>
            </TooltipTrigger>
          )
        }
      />

      <div className="flex-1 space-y-6 overflow-auto p-4 md:p-6 lg:p-8">
        {/* Overview Card */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('pages.simulations.run_details.overview')}
          </h3>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <InfoItem
              label={t('pages.simulations.run_details.scenario')}
              value={
                <button
                  type="button"
                  onClick={handleEditScenario}
                  className="group inline-flex items-center gap-1 text-left text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400 dark:hover:text-brand-300"
                  title={t('pages.simulations.run_details.edit_scenario')}
                >
                  <span>{scenario?.name ?? t('resources.simulations.scenario_fallback', { id: run.scenario_id })}</span>
                  <Edit01Icon className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              }
            />
            <InfoItem
              label={t('pages.simulations.run_details.assistant')}
              value={
                <Link
                  to={`/assistants/${run.assistant_id}`}
                  className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400 dark:hover:text-brand-300"
                  title={t('pages.simulations.run_details.view_assistant')}
                >
                  {assistant?.name ?? t('resources.simulations.assistant_fallback', { id: run.assistant_id })}
                </Link>
              }
            />
            <InfoItem
              label={t('pages.simulations.run_details.status')}
              value={<StatusBadge status={run.status} />}
            />
            <InfoItem
              label={t('pages.simulations.run_details.duration')}
              value={
                run.duration_seconds != null
                  ? `${run.duration_seconds.toFixed(1)}s`
                  : '-'
              }
            />
            {aiDeployment && (
              <InfoItem
                label={t('pages.simulations.run_details.ai_deployment')}
                value={
                  <Badge variant="gray" size="sm">
                    {aiDeployment.friendly_name || aiDeployment.model}
                  </Badge>
                }
              />
            )}
            {simulatorDeployment && (
              <InfoItem
                label={t('pages.simulations.run_details.simulator_deployment')}
                value={
                  <Badge variant="gray" size="sm">
                    {simulatorDeployment.friendly_name || simulatorDeployment.model}
                  </Badge>
                }
              />
            )}
            {judgeDeployment && (
              <InfoItem
                label={t('pages.simulations.run_details.judge_deployment')}
                value={
                  <Badge variant="gray" size="sm">
                    {judgeDeployment.friendly_name || judgeDeployment.model}
                  </Badge>
                }
              />
            )}
            {createdByUser && (
              <InfoItem
                label={t('pages.simulations.run_details.created_by')}
                value={createdByUser.name}
              />
            )}
          </div>
        </div>

        {/* Checks Card */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('pages.simulations.run_details.checks')}
          </h3>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
            <CheckItem
              label={t('pages.simulations.run_details.overall_result')}
              passed={run.overall_passed}
            />
            <CheckItem
              label={t('pages.simulations.run_details.tool_check')}
              passed={run.tool_check_passed}
            />
            <CheckItem
              label={t('pages.simulations.run_details.tool_param_check')}
              passed={run.tool_param_check_passed}
              subtitle={
                run.tool_param_error_count != null
                  ? `${run.tool_param_error_count} ${t('pages.simulations.run_details.tool_param_errors').toLowerCase()}`
                  : undefined
              }
            />
            <CheckItem
              label={t('pages.simulations.run_details.response_check')}
              passed={run.response_check_passed}
            />
            <CheckItem
              label={t('pages.simulations.run_details.llm_judge')}
              passed={run.llm_judge_passed}
            />
            <CheckItem
              label={t('pages.simulations.run_details.post_call_tool_check')}
              passed={run.post_call_tool_check_passed}
            />
          </div>

          {run.error && (
            <div className="mt-4 rounded-lg bg-error-50 p-4 dark:bg-error-900/20">
              <p className="text-sm font-medium text-error-700 dark:text-error-400">
                {t('pages.simulations.run_details.error')}
              </p>
              <p className="mt-1 text-sm text-error-600 dark:text-error-300">
                {run.error}
              </p>
            </div>
          )}
        </div>

        {/* LLM Judge Reason Card */}
        {run.llm_judge_reason && (
          <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('pages.simulations.run_details.llm_judge_reason')}
            </h3>
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800/50">
              <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
                {run.llm_judge_reason}
              </p>
            </div>
          </div>
        )}

        {/* Scenario Details Card */}
        {scenario && (
          <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('pages.simulations.run_details.scenario_details')}
            </h3>

            <div className="space-y-4">
              {scenario.tags && scenario.tags.length > 0 && (
                <div>
                  <p className="mb-1 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('pages.simulations.scenarios.fields.tags')}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {scenario.tags.map((tag) => (
                      <Badge key={tag.id} variant="gray" size="sm">
                        {tag.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="mb-1 text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('pages.simulations.scenarios.fields.scenario_prompt')}
                </p>
                <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
                  {scenario.scenario_prompt}
                </p>
              </div>

              {scenario.expected_tools && scenario.expected_tools.length > 0 && (
                <div>
                  <p className="mb-1 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('pages.simulations.scenarios.fields.expected_tools')}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {scenario.expected_tools.map((tool) => (
                      <Badge key={tool} variant="gray" size="sm">
                        {tool}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {scenario.llm_judge_criteria && (
                <div>
                  <p className="mb-1 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('pages.simulations.scenarios.fields.llm_judge_criteria')}
                  </p>
                  <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
                    {scenario.llm_judge_criteria}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Conversation Card */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('pages.simulations.run_details.conversation')}
          </h3>

          <ConversationViewer
            messages={run.messages}
            toolCalls={run.tool_calls}
            postProcessorToolCalls={run.post_processor_tool_calls}
          />
        </div>
      </div>

      {/* Scenario Edit Modal */}
      <ScenarioFormDialog
        isOpen={isScenarioModalOpen}
        onClose={() => setIsScenarioModalOpen(false)}
        onSubmit={handleScenarioSubmit}
        scenario={scenario}
        isLoading={updateScenario.isPending}
      />
    </div>
  )
}

interface InfoItemProps {
  label: string
  value: React.ReactNode
}

function InfoItem({ label, value }: InfoItemProps) {
  return (
    <div>
      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</p>
      <div className="mt-1">{value}</div>
    </div>
  )
}

interface CheckItemProps {
  label: string
  passed: boolean | null
  subtitle?: string
}

function CheckItem({ label, passed, subtitle }: CheckItemProps) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <div className="flex flex-col">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </span>
        {subtitle && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {subtitle}
          </span>
        )}
      </div>
      <PassFailBadge passed={passed} />
    </div>
  )
}

export default SimRunDetailsPage
